import { Download, FileText, Loader2, Trash2 } from "lucide-react";
import { useManagedFileOpener } from "@/hooks/useManagedFileOpener";
import { isPlainLeftClick } from "@/utils/managedFile";
import type { ManagedFileBrief } from "../types";

function fmtSize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function typeLabel(name: string, contentType: string): string {
  const ext = name.includes(".") ? name.split(".").pop() : "";
  if (ext && ext.length <= 5) return ext.toUpperCase();
  if (contentType) return (contentType.split("/").pop() ?? "plik").toUpperCase();
  return "PLIK";
}

/**
 * Dignified attachment cards: type-icon tile, filename, "TYPE · size", download affordance.
 * When `onDelete` is supplied, each card gains a delete button (for editors).
 *
 * The click goes through a link signed at that moment (useManagedFileOpener): `f.url`
 * is a presigned S3 link that dies an hour after the payload was built, and these
 * cards sit on pages that stay open far longer than that — a requisition waiting for
 * approval with the WPW packages from the mass sequence is exactly such a page. The
 * href keeps the stale link so "open in a new tab" and "copy link" still do something
 * sensible within the hour.
 */
export function AttachmentList({
  files,
  onDelete,
  deletingId,
}: {
  files: ManagedFileBrief[];
  onDelete?: (file: ManagedFileBrief) => void;
  deletingId?: string | null;
}) {
  const { openFile, busyId } = useManagedFileOpener();
  if (!files?.length) {
    return <p className="text-sm text-gray-400 dark:text-gray-500">Brak załączników.</p>;
  }
  return (
    <ul className="grid gap-2 sm:grid-cols-2">
      {files.map((f) => (
        <li key={f.id} className="flex items-stretch gap-1">
          <a
            href={f.url ?? "#"}
            download={f.original_filename}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
              if (!isPlainLeftClick(e)) return;
              e.preventDefault();
              void openFile(f);
            }}
            className="group flex min-w-0 flex-1 items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2.5 transition-all hover:border-[var(--color-primary)] hover:shadow-sm"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
              <FileText size={18} />
            </span>
            <span className="min-w-0 flex-1">
              <span
                className="block truncate text-sm font-medium text-gray-800 dark:text-gray-100"
                title={f.original_filename}
              >
                {f.original_filename}
              </span>
              <span className="block text-xs text-gray-400 dark:text-gray-500">
                {[typeLabel(f.original_filename, f.content_type), fmtSize(f.size_bytes)]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
            {busyId === f.id ? (
              <Loader2 size={16} className="shrink-0 animate-spin text-[var(--color-primary)]" />
            ) : (
              <Download
                size={16}
                className="shrink-0 text-gray-300 transition-colors group-hover:text-[var(--color-primary)]"
              />
            )}
          </a>
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(f)}
              disabled={deletingId === f.id}
              title="Usuń załącznik"
              aria-label={`Usuń załącznik ${f.original_filename}`}
              className="flex w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 transition-colors hover:border-red-300 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Trash2 size={15} />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
